<template>
    <component
        :is="component"
        :name="props.name"
        :with-variable-height="withVariableHeight"
        :without-padding="props.variant === 'inline'"
    >
        <flex-container v-if="contact">
            <flex-container v-if="props.variant === 'card'">
                <flex-container :size="5">
                    <user-avatar
                        :placeholder="contact.name.abbreviation"
                        :alt="contact.name.fullName"
                    />
                </flex-container>
                <flex-container :size="5" justify-content="flex-end">
                    <button-outline :disabled="!props.editable" @click="openContactEdit">
                        Wijzig
                    </button-outline>
                </flex-container>
            </flex-container>

            <flex-container>
                <flex-container size="full" :screen-sizes="{ desktop: 4 }">
                    <data-group>
                        <data-label>Volledige naam</data-label>
                        <data-value>
                            <div style="display: flex; gap: 3px">
                                <span>{{ contact.name }}</span>
                                <svg-icon
                                    v-if="genderIcon"
                                    :icon="genderIcon"
                                    :size="IconSize.sm"
                                />
                            </div>
                        </data-value>
                    </data-group>
                </flex-container>

                <flex-container size="full" :screen-sizes="{ desktop: 6 }">
                    <data-group v-if="props.variant === 'card' && !isPartnerWithoutPhoneNumber">
                        <data-label>Telefoon</data-label>
                        <data-value v-if="phoneNumber">
                            <a :href="`tel:${phoneNumber}`" :title="phoneNumber.toString()">{{ phoneNumber }}</a>
                        </data-value>
                        <data-value v-else>
                            Onbekend
                        </data-value>
                    </data-group>

                    <data-group v-if="props.variant === 'inline' && !isPartnerWithoutEmail">
                        <data-label>E-mail</data-label>
                        <data-value>
                            <copy-content-block>
                                {{ contact.email }}
                            </copy-content-block>
                        </data-value>
                    </data-group>
                </flex-container>
            </flex-container>

            <flex-container>
                <flex-container :size="4">
                    <data-group v-if="props.variant === 'inline' && !isPartnerWithoutPhoneNumber">
                        <data-label>Telefoon</data-label>
                        <data-value v-if="phoneNumber">
                            <a :href="`tel:${phoneNumber}`" :title="phoneNumber.toString()">{{ phoneNumber }}</a>
                        </data-value>
                        <data-value v-else>
                            Onbekend
                        </data-value>
                    </data-group>
                    <data-group v-if="props.variant === 'card' && !isPartnerWithoutEmail">
                        <data-label>E-mail</data-label>
                        <data-value>
                            <copy-content-block>
                                {{ contact.email }}
                            </copy-content-block>
                        </data-value>
                    </data-group>
                </flex-container>

                <flex-container v-if="contact.address?.fullAddress && props.variant === 'inline' && !isPartnerWithoutAddress" :size="6">
                    <data-group>
                        <data-label>Adres</data-label>
                        <data-value>
                            <div style="padding-bottom: 2px;">
                                {{ contact.address?.fullStreet }}
                            </div>
                            <div>
                                {{ contact.address?.postalCode }} {{ contact.address?.city }}
                            </div>
                        </data-value>
                    </data-group>
                </flex-container>
            </flex-container>

            <flex-container v-if="contact.address?.fullAddress && props.variant === 'card' && !isPartnerWithoutAddress">
                <data-group>
                    <data-label>Adres</data-label>
                    <data-value>
                        <div style="padding-bottom: 2px;">
                            {{ contact.address?.fullStreet }}
                        </div>
                        <div>
                            {{ contact.address?.postalCode }} {{ contact.address?.city }}
                        </div>
                    </data-value>
                </data-group>
            </flex-container>

            <flex-container v-if="!!contact.birthDate">
                <data-group>
                    <data-label>Geboortedatum</data-label>
                    <data-value>{{ formatDate(contact.birthDate) }}</data-value>
                </data-group>
            </flex-container>

            <template v-if="!isPartner">
                <flex-container v-if="contact.futureAddress?.fullAddress">
                    <data-group>
                        <data-label>Toekomstig adres</data-label>
                        <data-value>
                            <div style="padding-bottom: 2px;">
                                {{ contact.futureAddress?.fullStreet }}
                            </div>
                            <div>
                                {{ contact.futureAddress?.postalCode }} {{ contact.futureAddress?.city }}
                            </div>
                        </data-value>
                    </data-group>
                </flex-container>

                <flex-container v-else-if="props.editable">
                    <data-group>
                        <data-label>Toekomstig adres</data-label>
                        <button-text @click="openFutureAddressEdit">
                            <svg-icon :icon="IconAdd" />
                            <span>Adres toevoegen</span>
                        </button-text>
                    </data-group>
                </flex-container>
            </template>

            <!-- Uitgeschakeld voor nu -->
            <!-- <div style="display: flex; justify-content: end">
                <button-outline-icon
                    :icon="IconUserSend"
                    :color="Color.brandGreen"
                    is-custom-icon
                />
            </div> -->
        </flex-container>
    </component>
</template>

<script setup lang="ts">
    import { computed } from 'vue';
    import { Contact, Gender } from '@keyvue/one-api-client';
    import {
        SvgIcon,
        DataGroup,
        DataLabel,
        DataValue,
        IconSize,
        FlexContainer,
        ButtonOutline,
        InlineTabPanel,
        CardTabPanel,
        UserAvatar,
    } from '@keyvue/one-ui';

    import CopyContentBlock from '@components/Blocks/CopyContentBlock.vue';

    import IconMale from '@keyvue/one-material-icons/icons/male.svg?component';
    import IconFemale from '@keyvue/one-material-icons/icons/female.svg?component';
    import IconAdd from '@keyvue/one-material-icons/icons/add.svg?component';
    import { useRouter, useRoute, type RouteLocationRaw } from 'vue-router';
    import { formatDate } from '@/utils/date';

    export interface Props {
        contact: Contact
        variant: 'inline' | 'card'
        name: string
        withVariableHeight?: boolean
        editable?: boolean,
    }

    const props = defineProps<Props>();
    const router = useRouter();
    const route = useRoute();

    const phoneNumber = computed(() => (props.contact.phoneNumber ?? props.contact.mobilePhoneNumber));
    const component = computed(() => (props.variant === 'inline' ? InlineTabPanel : CardTabPanel));
    const isPartner = computed(() => !!props.contact.primaryContact);

    const isPartnerWithoutPhoneNumber = computed(() => isPartner.value && !phoneNumber.value);
    const isPartnerWithoutEmail = computed(() => isPartner.value && !props.contact.email);
    const isPartnerWithoutAddress = computed(() => isPartner.value && !props.contact.address);

    const genderIcon = computed(() => {
        if (!props.contact.gender) {
            return null;
        }

        if (props.contact.gender.equals(Gender.MALE)) {
            return IconMale;
        }

        if (props.contact.gender.equals(Gender.FEMALE)) {
            return IconFemale;
        }

        return null;
    });

    const routeToContactEdit = computed(() => {
        const contactEditRoute: RouteLocationRaw = {
            name: 'contactEdit',
            params: {
                contactId: route.params.contactId
                    ? route.params.contactId
                    : props.contact.id.toString(),
            },
            query: {
                contact: isPartner.value ? 'partner' : 'contact',
            },
        };

        if ('leadId' in route.params) {
            contactEditRoute.name = 'leadContactEdit';
            contactEditRoute.params = {
                ...contactEditRoute.params,
                leadId: route.params.leadId.toString(),
            };
        }

        if ('orderId' in route.params) {
            contactEditRoute.name = 'orderContactEdit';
            contactEditRoute.params = {
                ...contactEditRoute.params,
                orderId: route.params.orderId.toString(),
            };
        }

        return contactEditRoute;
    });

    const openContactEdit = () => {
        router.push(routeToContactEdit.value);
    };

    const openFutureAddressEdit = () => {
        router.push({
            ...routeToContactEdit.value,
            query: {
                address: 'future-address',
            },
        });
    };
</script>
